import { useEffect, useId, useRef, useState } from "react";
import { searchIdentityUsers } from "@/api/identityUsers";
import { ApiError } from "@/api/client";
import { FieldLabel, TextInput } from "@/components/admin/AdminShell";
import { useClickOutside } from "@/hooks/useClickOutside";
import { detectIdentityUserQuery } from "@/lib/identity/searchUsers";
import type { IdentityUser } from "@/types/identityUser";

const DEBOUNCE_MS = 300;

function contactLine(user: IdentityUser): string {
  return [user.email, user.phone].filter(Boolean).join(" · ");
}

export function IdentityUserPicker({
  value,
  onChange,
  disabled,
  label = "Пользователь",
}: {
  value: IdentityUser | null;
  onChange: (user: IdentityUser | null) => void;
  disabled?: boolean;
  label?: string;
}) {
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<IdentityUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  useClickOutside(rootRef, () => setOpen(false), open);

  useEffect(() => {
    if (value) {
      setQuery("");
      setResults([]);
      setOpen(false);
      setSearched(false);
      setError(null);
    }
  }, [value]);

  useEffect(() => {
    if (value || disabled) return;
    const searchQuery = detectIdentityUserQuery(query);
    if (!searchQuery) {
      setResults([]);
      setLoading(false);
      setSearched(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    const timer = window.setTimeout(() => {
      void searchIdentityUsers(query)
        .then((list) => {
          if (cancelled) return;
          setResults(list);
          setSearched(true);
          setOpen(true);
        })
        .catch((err) => {
          if (cancelled) return;
          setResults([]);
          setSearched(true);
          setOpen(true);
          setError(
            err instanceof ApiError
              ? err.message
              : "Не удалось найти пользователя",
          );
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, value, disabled]);

  const selectUser = (user: IdentityUser) => {
    onChange(user);
    setQuery("");
    setResults([]);
    setOpen(false);
    setSearched(false);
  };

  return (
    <div ref={rootRef} className="relative">
      <FieldLabel htmlFor="identity-user-search" size="compact">
        {label}
      </FieldLabel>

      {value ? (
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
          <div className="min-w-0 flex-1">
            <p className="truncate font-onest text-sm font-medium text-slate-900">
              {value.name}
            </p>
            {contactLine(value) ? (
              <p className="truncate font-onest text-xs text-slate-500">
                {contactLine(value)}
              </p>
            ) : null}
            <p className="truncate font-mono text-[11px] text-slate-400">
              {value.id}
            </p>
          </div>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange(null)}
            className="shrink-0 font-onest text-xs font-medium text-slate-600 hover:text-slate-900 disabled:opacity-50"
          >
            Изменить
          </button>
        </div>
      ) : (
        <>
          <TextInput
            id="identity-user-search"
            size="compact"
            value={query}
            disabled={disabled}
            placeholder="Email, телефон или CHM ID"
            autoComplete="off"
            onChange={(next) => {
              setQuery(next);
              setOpen(true);
            }}
          />
          {loading ? (
            <p className="mt-1 font-onest text-[11px] text-slate-500">Поиск…</p>
          ) : null}

          {open && (results.length > 0 || (searched && !loading)) ? (
            <ul
              id={listboxId}
              role="listbox"
              className="absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg"
            >
              {error ? (
                <li className="px-3 py-2.5 font-onest text-sm text-red-600">
                  {error}
                </li>
              ) : results.length === 0 ? (
                <li className="px-3 py-2.5 font-onest text-sm text-slate-500">
                  Никого не найдено. Укажите почту, телефон или CHM ID.
                </li>
              ) : (
                results.map((user) => {
                  const subtitle = contactLine(user);
                  return (
                    <li key={user.id} role="option">
                      <button
                        type="button"
                        onClick={() => selectUser(user)}
                        className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-slate-50"
                      >
                        <span className="font-onest text-sm font-medium text-slate-900">
                          {user.name}
                        </span>
                        {subtitle ? (
                          <span className="font-onest text-xs text-slate-500">
                            {subtitle}
                          </span>
                        ) : null}
                        <span className="font-mono text-[11px] text-slate-400">
                          {user.id}
                        </span>
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          ) : null}
        </>
      )}
    </div>
  );
}
